/**
 * ProspectConfirmationDialog Component
 * 
 * Displays a confirmation dialog when staff changes an inquiry status to REVIEWING.
 * Allows staff to verify and edit contact information before creating the client account.
 * This creates a PROSPECT user with access to documents and chat.
 * 
 * Requirements: 1.3, 1.4, 1.5, 1.6, 1.8
 */

import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

export interface ContactInfo {
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
}

interface ValidationErrors {
  email?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
}

interface ProspectConfirmationDialogProps {
  open: boolean;
  inquiry: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
  };
  onConfirm: (editedContactInfo: ContactInfo) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

/**
 * Validates email format
 */
function isValidEmail(email: string): boolean {
  // Stricter email regex - only allows alphanumeric, dots, hyphens, underscores, and plus signs
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
}

/**
 * Validates phone number format (basic validation)
 */
function isValidPhoneNumber(phone: string): boolean {
  // Allow various phone formats: (123) 456-7890, 123-456-7890, 1234567890, +1234567890
  const phoneRegex = /^[\d\s\-\(\)\+]+$/;
  const digitsOnly = phone.replace(/\D/g, '');
  return phoneRegex.test(phone) && digitsOnly.length >= 10;
}

export function ProspectConfirmationDialog({
  open,
  inquiry,
  onConfirm,
  onCancel,
  isLoading = false,
}: ProspectConfirmationDialogProps) {
  const [contactInfo, setContactInfo] = useState<ContactInfo>({
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
  });
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Initialize form with inquiry data when dialog opens
  useEffect(() => {
    if (open && inquiry) {
      setContactInfo({
        email: inquiry.email || '',
        firstName: inquiry.firstName || '',
        lastName: inquiry.lastName || '',
        phoneNumber: inquiry.phoneNumber || '',
      });
      setErrors({});
      setTouched({});
    }
  }, [open, inquiry]);

  // Validate a single field
  const validateField = (field: keyof ContactInfo, value: string): string | undefined => {
    switch (field) {
      case 'email':
        if (!value.trim()) return 'Email is required';
        if (!isValidEmail(value)) return 'Please enter a valid email address';
        return undefined;
      case 'firstName':
        if (!value.trim()) return 'First name is required';
        return undefined;
      case 'lastName':
        if (!value.trim()) return 'Last name is required';
        return undefined;
      case 'phoneNumber':
        if (!value.trim()) return 'Phone number is required';
        if (!isValidPhoneNumber(value)) return 'Please enter a valid phone number';
        return undefined;
      default:
        return undefined;
    }
  };

  // Validate all fields
  const validateAll = (): ValidationErrors => {
    const newErrors: ValidationErrors = {};
    (Object.keys(contactInfo) as Array<keyof ContactInfo>).forEach((field) => {
      const error = validateField(field, contactInfo[field]);
      if (error) {
        newErrors[field] = error;
      }
    });
    return newErrors;
  };

  // Handle field change
  const handleChange = (field: keyof ContactInfo, value: string) => {
    setContactInfo((prev) => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (touched[field]) {
      const error = validateField(field, value);
      setErrors((prev) => ({ ...prev, [field]: error }));
    }
  };

  // Handle field blur
  const handleBlur = (field: keyof ContactInfo) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const error = validateField(field, contactInfo[field]);
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  // Handle form submission
  const handleConfirm = async () => {
    // Mark all fields as touched
    setTouched({
      email: true,
      firstName: true,
      lastName: true,
      phoneNumber: true,
    });

    // Validate all fields
    const validationErrors = validateAll();
    setErrors(validationErrors);

    // If there are errors, don't submit
    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    // Call the onConfirm callback with edited contact info
    await onConfirm(contactInfo);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Create Client Account</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Info Banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-900 font-medium mb-2">
              This will create a client account and set the inquiry status to REVIEWING.
            </p>
            <ul className="text-sm text-blue-800 list-disc list-inside space-y-1">
              <li>The client will receive an email with a temporary password</li>
              <li>They can log in to view their inquiry status</li>
              <li>They can upload documents and message your team</li>
              <li>The inquiry status will change to REVIEWING</li>
            </ul>
          </div>

          {/* Contact Information Form */}
          <div className="space-y-4">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <Input
                id="email"
                type="email"
                value={contactInfo.email}
                onChange={(e) => handleChange('email', e.target.value)}
                onBlur={() => handleBlur('email')}
                className={errors.email && touched.email ? 'border-red-500' : ''}
                placeholder="Enter email address"
                disabled={isLoading}
              />
              {errors.email && touched.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            {/* First Name */}
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                First Name <span className="text-red-500">*</span>
              </label>
              <Input
                id="firstName"
                type="text"
                value={contactInfo.firstName}
                onChange={(e) => handleChange('firstName', e.target.value)}
                onBlur={() => handleBlur('firstName')}
                className={errors.firstName && touched.firstName ? 'border-red-500' : ''}
                placeholder="Enter first name"
                disabled={isLoading}
              />
              {errors.firstName && touched.firstName && (
                <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
              )}
            </div>

            {/* Last Name */}
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                Last Name <span className="text-red-500">*</span>
              </label>
              <Input
                id="lastName"
                type="text"
                value={contactInfo.lastName}
                onChange={(e) => handleChange('lastName', e.target.value)}
                onBlur={() => handleBlur('lastName')}
                className={errors.lastName && touched.lastName ? 'border-red-500' : ''}
                placeholder="Enter last name"
                disabled={isLoading}
              />
              {errors.lastName && touched.lastName && (
                <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
              )}
            </div>

            {/* Phone Number */}
            <div>
              <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <Input
                id="phoneNumber"
                type="tel"
                value={contactInfo.phoneNumber}
                onChange={(e) => handleChange('phoneNumber', e.target.value)}
                onBlur={() => handleBlur('phoneNumber')}
                className={errors.phoneNumber && touched.phoneNumber ? 'border-red-500' : ''}
                placeholder="Enter phone number"
                disabled={isLoading}
              />
              {errors.phoneNumber && touched.phoneNumber && (
                <p className="mt-1 text-sm text-red-600">{errors.phoneNumber}</p>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              onClick={handleConfirm}
              disabled={isLoading}
              className="flex-1"
            >
              {isLoading ? 'Creating Account...' : 'Confirm & Create Account'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isLoading}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
